import { useEffect, useState } from "react";
import { getAuthHeader } from "../../api/authToken.js";
import { useParams } from "react-router-dom";
import "../../styles/App.css";
import { ZONE_OPTIONS } from "../Allocation/DefaultZoneBlock.js";
import { PRIVILEGE_OPTIONS } from "../Allocation/PrivilegeSelect.js";
import GroupLabel from "../../components/GroupLabel.js";
import { PERSON_GENDER_LABELS } from "../../decision/allocationLabels.js";

const COURSE_OPTIONS = [1, 2, 3, 4, 5, 6];
const NOT_SET = <span className="missing-value">не вказано</span>;

const StudentPage = () => {
  const { id } = useParams();
  const [student, setStudent] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});
  const [loading, setLoading] = useState(true);
  const API = process.env.REACT_APP_API_BASE;

  const [ratingList, setRatingList] = useState([]);

  useEffect(() => {
    const fetchStudent = async () => {
      try {
        const response = await fetch(`${API}/api/students/${id}`, {
          headers: { Authorization: getAuthHeader() },
        });
        const data = await response.json();
        setStudent(data);

        // editData із значеннями або порожніми рядками
        setEditData({
          pib: data.pib || "",
          group: data.group || "",
          room_number: data.room_number || "",
          rating: data.rating || "0",
          privilege_level: data.privilege_level ?? 1,
          home_zone: data.home_zone ?? "",
          gender: data.gender ?? "",
          course: data.course ?? "",
          violations: data.violations || "",
          acts_count: data.acts_count || "",
          additional_info: data.additional_info || "",
        });

        setLoading(false);
      } catch (error) {
        console.error("Error fetching student data:", error);
      }
    };

    fetchStudent();
    fetchRating(id);
  }, [id]);

  const fetchRating = async (studentId) => {
    try {
      const response = await fetch(`${API}/api/rating/${studentId}`, {
        headers: { Authorization: getAuthHeader() },
      });
      if (response.ok) {
        const data = await response.json();
        setRatingList(data);
      } else {
        alert("Не вдалося завантажити рейтинг.");
      }
    } catch (error) {
      console.error("Помилка завантаження рейтингу:", error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    const sanitizedData = {
      pib: editData.pib ?? null,
      group: editData.group ?? null,
      room_number: editData.room_number ?? null,
      rating: editData.rating ?? null,
      privilege_level: Number(editData.privilege_level) || 1,
      home_zone: editData.home_zone === "" ? null : Number(editData.home_zone),
      gender: editData.gender === "" ? null : editData.gender,
      course: editData.course === "" ? null : Number(editData.course),
      violations: editData.violations ?? null,
      acts_count: editData.acts_count ?? null,
      additional_info: editData.additional_info ?? null,
    };

    try {
      const response = await fetch(`${API}/api/students/${id}/update`, {
        method: "PUT",
        headers: {
          Authorization: getAuthHeader(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify(sanitizedData),
      });

      if (response.ok) {
        const updatedStudent = await response.json();
        setStudent(updatedStudent);
        alert("Інформацію успішно оновлено!");
        window.location.reload();
      } else {
        alert("Помилка оновлення даних!");
      }
    } catch (error) {
      console.error("Помилка оновлення:", error);
      alert("Не вдалося оновити дані.");
    }
  };

  if (loading) {
    return <p className="page-container">Завантаження...</p>;
  }

  if (!student.pib) {
    return <p className="page-container">Студента не знайдено</p>;
  }

  return (
    <div className="page-container">
      <h3 className="text-header">
        {editMode ? "Редагування студента:" : "Особиста інформація студента:"}
      </h3>
      {editMode ? (
        <div className="input-component">
          <form>
            <div className="form-container">
              <div className="input-block text-header">
                <p className="text-header">ПІБ:</p>
                <input
                  type="text"
                  name="pib"
                  value={editData.pib || ""}
                  onChange={handleChange}
                  className="input-component-with-border"
                />
              </div>
              <div className="input-block text-header">
                <p className="text-header">Група:</p>
                <input
                  type="text"
                  name="group"
                  value={editData.group || ""}
                  onChange={handleChange}
                  className="input-component-with-border"
                />
              </div>
            </div>
            <p className="text-header">
              <strong>Кімнату</strong> можна змінити в "перепоселення"
            </p>

            <div className="form-container">
              <div className="input-block text-header">
                <p className="text-header">Рейтинг:</p>
                <input
                  type="number"
                  name="rating"
                  value={editData.rating || ""}
                  onChange={handleChange}
                  className="input-component-with-border"
                />
                <p className="text-header">
                  Від 0 до 100. Після імпорту рейтингу тут сума балів (сесія + гуртожиток + діяльність).
                </p>
              </div>

              <div className="input-block text-header">
                <p className="text-header">Пільга:</p>
                <select
                  name="privilege_level"
                  value={editData.privilege_level}
                  onChange={handleChange}
                  className="header-content-select"
                >
                  {PRIVILEGE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="input-block text-header">
                <p className="text-header">Віддаленість від дому:</p>
                <select
                  name="home_zone"
                  value={editData.home_zone}
                  onChange={handleChange}
                  className="header-content-select"
                >
                  <option value="">Не вказано</option>
                  {ZONE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-container">
              <div className="input-block text-header">
                <label className="text-header" htmlFor="student-gender">
                  Стать:
                </label>
                <select
                  id="student-gender"
                  name="gender"
                  value={editData.gender}
                  onChange={handleChange}
                  className="header-content-select"
                >
                  <option value="">Не вказано</option>
                  {Object.entries(PERSON_GENDER_LABELS).map(([value, label]) => (
                    <option key={value} value={value}>
                      {label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="input-block text-header">
                <label className="text-header" htmlFor="student-course">
                  Курс:
                </label>
                <select
                  id="student-course"
                  name="course"
                  value={editData.course}
                  onChange={handleChange}
                  className="header-content-select"
                >
                  <option value="">Не вказано</option>
                  {COURSE_OPTIONS.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-header">
              Без статі й курсу студент і його кімната не враховуються в плані розподілу.
            </p>

            <div>
              <p className="text-header">Додаткова інформація:</p>
              <textarea
                name="additional_info"
                value={editData.additional_info || ""}
                onChange={handleChange}
                className="input-component-with-border"
                style={{ width: "97%" }}
              />
            </div>

            <div className="header-content">
              <button
                onClick={handleSave}
                style={{
                  width: "50%",
                }}
                className="restore-button"
              >
                Зберегти
              </button>
              <button
                onClick={() => setEditMode(false)}
                style={{
                  width: "50%",
                }}
                className="remove-button"
              >
                Скасувати
              </button>
            </div>
          </form>
        </div>
      ) : (
        <div className="input-component">
          <div className="form-container">
            <p className="input-block text-header">
              <strong>ПІБ:</strong> {student.pib}
            </p>
            <p className="input-block text-header">
              <strong>Група:</strong> <GroupLabel group={student.group} />
            </p>
            <p className="input-block text-header">
              <strong>Поточна кімната:</strong> {student.dormitory_name} -{" "}
              {student.current_room}
            </p>
          </div>

          <div className="form-container" data-testid="student-model-data">
            <p className="input-block text-header">
              <strong>Віддаленість від дому:</strong>{" "}
              {ZONE_OPTIONS.find((o) => o.value === student.home_zone)?.label || "Не вказано"}
            </p>
            <p className="input-block text-header">
              <strong>Пільга:</strong>{" "}
              {PRIVILEGE_OPTIONS.find((o) => o.value === student.privilege_level)?.label || "Не вказано"}
            </p>
          </div>

          <div className="form-container" data-testid="student-constraints">
            <p className="input-block text-header">
              <strong>Стать:</strong> {PERSON_GENDER_LABELS[student.gender] || NOT_SET}
            </p>
            <p className="input-block text-header">
              <strong>Курс:</strong> {student.course || NOT_SET}
            </p>
          </div>

          <div className="form-container">
            <p className="input-block text-header">
              <strong>Рейтинг:</strong> {student.rating || "Немає інформації"}
              {student.latestScores &&
                ` (сесія ${student.latestScores.ses} · гуртожиток ${student.latestScores.dorm} · діяльність ${student.latestScores.act}; ${new Date(
                  student.latestScores.periodStart
                ).toLocaleDateString("uk-UA")} — ${new Date(student.latestScores.periodEnd).toLocaleDateString(
                  "uk-UA"
                )}, ${student.latestScores.confirmed ? "підтверджено" : "не підтверджено"})`}
            </p>
            <p className="input-block text-header">
              <strong>Додаткова інформація:</strong>{" "}
              {student.additional_info || "Немає інформації"}
            </p>
          </div>

          <div className="form-container">
            <p className="input-block text-header">
              Попередження
              <strong>{student.violations || "Немає попереджень"}</strong>
            </p>
            <p className="input-block text-header">
              Акти
              <strong>{student.acts_count || "Немає актів"}</strong>
            </p>
          </div>

          <button onClick={() => setEditMode(true)} className="submit-button">
            Редагувати
          </button>
        </div>
      )}
      <h3 className="text-header">Історія переселень</h3>
      <ul className="input-component" style={{ paddingLeft: "30px" }}>
        {student.relocationHistory && student.relocationHistory.length > 0 ? (
          student.relocationHistory.map((entry, index) => (
            <li key={index}>
              {new Date(entry.date).toLocaleDateString("uk-UA")}:{entry.from} →{" "}
              {entry.to}
            </li>
          ))
        ) : (
          <p>Студент ще не переселявся.</p>
        )}
      </ul>
      <h3 className="text-header">Список документів з рейтингом:</h3>
      {ratingList.length > 0 ? (
        <table className="table">
          <thead>
            <tr>
              <th className="table-header">Дата завантаження</th>
              <th className="table-header">Опис</th>
              <th className="table-header">Кількість балів</th>
              <th className="table-header">Посилання на документ</th>
            </tr>
          </thead>
          <tbody>
            {ratingList.map((rating) => (
              <tr key={rating.id}>
                <td className="table-content">
                  {new Date(rating.created_at).toLocaleDateString()}
                </td>
                <td className="table-content">{rating.description}</td>
                <td className="table-content">{rating.points}</td>
                <td className="table-content">
                  {rating.file_url ? (
                    <a
                      href={rating.file_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="violation-link"
                    >
                      Відкрити акт
                    </a>
                  ) : (
                    "Немає файлу"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p>У цього студента немає заслуг.</p>
      )}
      <h3 className="text-header">Список порушень та актів:</h3>
      {student.violationsDetails && student.violationsDetails.length > 0 ? (
        <table className="table">
          <thead>
            <tr>
              <th className="table-header">Дата</th>
              <th className="table-header">Опис порушення</th>
              <th className="table-header">Тип</th>
              <th className="table-header">Посилання на акт</th>
            </tr>
          </thead>
          <tbody>
            {student.violationsDetails.map((violation, index) => (
              <tr key={index}>
                <td className="table-content">
                  {new Date(violation.date).toLocaleDateString()}
                </td>
                <td className="table-content">{violation.reason}</td>
                <td className="table-content">
                  {violation.type === "act"
                    ? "Акт"
                    : violation.type === "warning"
                    ? "Попередження"
                    : "Невідомий тип"}
                </td>
                <td className="table-content">
                  {violation.file_url ? (
                    <a
                      href={violation.file_url}
                      className="violation-link"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Відкрити акт
                    </a>
                  ) : (
                    "Немає файлу"
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <p>У цього студента немає актів.</p>
      )}
    </div>
  );
};

export default StudentPage;
